import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation, WidgetProps } from '@wix/yoshi-flow-editor';
import { FormViewerContext, FormViewerHandle } from '@wix/form-viewer/widget';
import { FormValues } from '@wix/form-viewer';
import { Text } from 'wix-ui-tpa';
import { st, classes } from './Widget.st.css';
import { WaiverForms, WaiverFormStatus } from '../../../types/form-types';
import WaiverForm from './WaiverForm';
import { ViewMode } from '../controller';
import { type FormSubmission } from '@wix/ambassador-forms-v4-submission/types';

export type ControllerProps = {
  status: WaiverFormStatus;
  formId?: string;
  waiverForms?: {
    initialValues: Record<string, any>;
    overrides: Record<string, any>;
    fieldProperties: Record<string, any>;
  };
  formErrors?: { messageKey: string; field: string }[];
  onSubmitSuccess?: (submission: FormSubmission) => void;
  onLightboxClose?: () => void;
  viewMode?: ViewMode;
};

const Widget: FC<WidgetProps<ControllerProps>> = ({
  status,
  formId,
  waiverForms,
  formErrors,
  onSubmitSuccess,
  onLightboxClose,
  viewMode,
  ...widgetProps
}) => {
  const { t } = useTranslation();

  // Form values managed locally, state comes from controller
  const [waiverFormValues, setWaiverFormValues] = useState<FormValues>(
    waiverForms?.initialValues || {},
  );

  // Refs
  const waiverFormRef = useRef<FormViewerHandle>(null);

  // Update form values when controller state changes
  useEffect(() => {
    if (waiverForms?.initialValues) {
      setWaiverFormValues(waiverForms.initialValues);
    }
  }, [waiverForms?.initialValues]);

  const handleWaiverFormChange = useCallback((values: FormValues) => {
    setWaiverFormValues(values);
  }, []);

  return (
    <div data-hook="WaiverForm-wrapper" className={st(classes.root)}>
      <div className={classes.formContainer}>
        {status === WaiverFormStatus.INITIALIZING && (
          <div className={classes.loadingContainer}>
            <Text className={classes.loadingText}>
              {t('waivers-sign-form.loading')}
            </Text>
          </div>
        )}

        {status === WaiverFormStatus.ERROR && (
          <div className={classes.errorContainer}>
            <Text className={classes.errorText}>
              {formErrors && formErrors.length > 0
                ? formErrors.map((error, index) => (
                    <Text key={index} tagName="div">
                      {t(error.messageKey)}
                    </Text>
                  ))
                : t('waivers-sign-form.error-loading')}
            </Text>
          </div>
        )}

        {status === WaiverFormStatus.IDLE && formId && waiverForms && (
          <FormViewerContext
            host={widgetProps.host}
            wfActions={(widgetProps as any).wfActions}
            wfConfig={(widgetProps as any).wfConfig}
            wfResults={(widgetProps as any).wfResults}
            wfActionsV2={(widgetProps as any).wfActionsV2}
            wfResultsV2={(widgetProps as any).wfResultsV2}
          >
            <WaiverForm
              formId={formId}
              waiverFormRef={waiverFormRef}
              waiverForms={waiverForms as WaiverForms}
              formValues={waiverFormValues}
              handleFormChange={handleWaiverFormChange}
              onSubmitSuccess={onSubmitSuccess}
              onLightboxClose={onLightboxClose}
              viewMode={viewMode}
            />
          </FormViewerContext>
        )}
      </div>
    </div>
  );
};

export default Widget;
